import { useState } from 'react';
import { Timestamp } from 'firebase/firestore';
import { toast } from 'sonner';
import { Plus, X } from 'lucide-react';

import {
  createTournament,
  isTournamentSlugTaken,
  slugify,
  updateTournament,
} from '../../../database/tournaments';
import { createStages } from '../../../database/tournamentStages';
import { uploadToStorage } from '../../../database/storage';
import {
  isValidExternalUrl,
  isValidParticipationUrl,
  normalizePrizePool,
  normalizeSponsors,
} from '../../Public/tournament/tournamentHelpers';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { NativeSelect } from '../../ui/select-native';
import { Textarea } from '../../ui/textarea';
import { cn } from '../../../lib/utils';
import {
  DEFAULT_VISIBILITY,
  STAGED_FORMAT,
  STAGE_PRESETS,
  stageTypeLabel,
} from '../../../lib/stages';
import {
  DEFAULT_GAME,
  DEFAULT_MODE,
  GAME_LABELS,
  GAME_WITHOUT_MODE,
  GAMES,
  MODES,
  modeLabel,
} from '../../../constants/modes';
import PrizePoolEditor from './PrizePoolEditor';
import TournamentRulesEditor from './TournamentRulesEditor';

export const EMPTY_FORM = {
  name: '',
  slug: '',
  description: '',
  participation: '',
  streamUrl: '',
  format: STAGED_FORMAT,
  status: 'upcoming',
  startDate: new Date(),
  endDate: new Date(),
  rules: '',
  imageUrl: '',
  tournamentLogo: '',
  sponsors: [],
  prizePool: { status: 'none', amount: '', currency: 'USD', contributors: [] },
  visibility: DEFAULT_VISIBILITY,
  votingOpen: false,
  communityAverageVisible: false,
  game: DEFAULT_GAME,
  mode: DEFAULT_MODE,
  modes: [DEFAULT_MODE],
};

export const toDate = (v) =>
  v && typeof v.toDate === 'function' ? v.toDate() : v ? new Date(v) : new Date();

const toDateTimeLocal = (d) => {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Sanitize a slug while the admin is still typing: lowercase + collapse
// invalid chars to dashes, but DON'T trim a trailing dash (so the next word
// can be typed). slugify() does the final trim on blur / save.
const softSlug = (value) =>
  (value || '')
    .toString()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-');

export const tournamentFromDoc = (t) => {
  if (!t) return EMPTY_FORM;
  const legacyMode16 = !t.game && t.mode === '1.6kz';
  const resolvedMode = legacyMode16 ? DEFAULT_MODE : t.mode || DEFAULT_MODE;
  const resolvedModes =
    Array.isArray(t.modes) && t.modes.length > 0
      ? t.modes
      : resolvedMode
        ? [resolvedMode]
        : [DEFAULT_MODE];
  return {
    ...EMPTY_FORM,
    ...t,
    game: t.game === null ? '' : t.game || (legacyMode16 ? '1.6kz' : DEFAULT_GAME),
    mode: resolvedMode,
    modes: resolvedModes,
    startDate: toDate(t.startDate),
    endDate: toDate(t.endDate),
    sponsors: normalizeSponsors(t.sponsors || []),
    prizePool: normalizePrizePool(t.prizePool, t),
  };
};

const Field = ({ label, required, hint, children }) => (
  <div className="flex flex-col gap-2">
    <span className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">
      {label}
      {required && <span className="ml-1 text-heat">*</span>}
    </span>
    {children}
    {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
  </div>
);

const TournamentSettingsForm = ({ existing, onSaved, onCancel }) => {
  const [form, setForm] = useState(() => tournamentFromDoc(existing));
  const [imageUpload, setImageUpload] = useState(null);
  const [logoUpload, setLogoUpload] = useState(null);
  const [saving, setSaving] = useState(false);
  const [presetId, setPresetId] = useState(STAGE_PRESETS[0].id);

  const editing = !!existing;

  const modesDisabled = !form.game || form.game === GAME_WITHOUT_MODE;
  let modesHint = 'Pick one or more. Shown in the tournament header.';
  if (!form.game) modesHint = 'Not applicable without a game.';
  else if (form.game === GAME_WITHOUT_MODE) modesHint = 'Not applicable for 1.6 KZ.';

  const patch = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  const patchValue = (key) => (e) =>
    setForm((f) => ({
      ...f,
      [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
    }));
  const addSponsor = () =>
    setForm((f) => ({
      ...f,
      sponsors: [...(f.sponsors || []), { name: '', logoUrl: '', link: '' }],
    }));
  const updateSponsor = (index, key, value) =>
    setForm((f) => ({
      ...f,
      sponsors: (f.sponsors || []).map((sponsor, sponsorIndex) =>
        sponsorIndex === index ? { ...sponsor, [key]: value } : sponsor
      ),
    }));
  const removeSponsor = (index) =>
    setForm((f) => ({
      ...f,
      sponsors: (f.sponsors || []).filter((_, sponsorIndex) => sponsorIndex !== index),
    }));
  const updateSponsorLogo = (index, file) =>
    setForm((f) => ({
      ...f,
      sponsors: (f.sponsors || []).map((sponsor, sponsorIndex) =>
        sponsorIndex === index ? { ...sponsor, logoFile: file } : sponsor
      ),
    }));

  // Editing the name keeps the slug in sync only while it still matches the
  // name (or is empty) — once an admin hand-edits the slug, it sticks.
  const handleNameChange = (e) => {
    const name = e.target.value;
    setForm((f) => {
      const autoTracked = !f.slug || f.slug === slugify(f.name);
      return { ...f, name, slug: autoTracked ? slugify(name) : f.slug };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Name is required.');
      return;
    }
    const slug = slugify(form.slug || form.name);
    if (!slug) {
      toast.error('Could not derive a URL slug — add letters or numbers to the name or slug.');
      return;
    }
    const participation = (form.participation || '').trim();
    if (participation && !isValidParticipationUrl(participation)) {
      toast.error('Participation link must be a valid http(s) URL or internal path.');
      return;
    }
    const streamUrl = (form.streamUrl || '').trim();
    if (streamUrl && !isValidExternalUrl(streamUrl)) {
      toast.error('Stream link must be a valid http(s) URL.');
      return;
    }
    setSaving(true);
    try {
      if (await isTournamentSlugTaken(slug, existing?.id)) {
        toast.error(`The slug "${slug}" is already used by another tournament.`);
        setSaving(false);
        return;
      }

      let imageUrl = form.imageUrl;
      let tournamentLogo = form.tournamentLogo;
      let sponsors = (form.sponsors || []).map((sponsor) => ({
        name: sponsor.name || '',
        logoUrl: sponsor.logoUrl || '',
        link: sponsor.link || '',
      }));

      if (imageUpload) imageUrl = await uploadToStorage(imageUpload, 'tournament-images');
      if (logoUpload) tournamentLogo = await uploadToStorage(logoUpload, 'tournament-logos');
      for (let index = 0; index < (form.sponsors || []).length; index += 1) {
        const file = form.sponsors[index]?.logoFile;
        if (!file) continue;
        const logoUrl = await uploadToStorage(file, 'tournament-sponsors');
        if (sponsors[index]) sponsors[index] = { ...sponsors[index], logoUrl };
      }
      sponsors = normalizeSponsors(sponsors);

      const cleanedModes = modesDisabled ? [] : (form.modes || []).filter(Boolean);
      const payload = {
        ...form,
        slug,
        participation,
        streamUrl,
        format: STAGED_FORMAT,
        game: form.game || null,
        imageUrl,
        tournamentLogo,
        sponsors,
        prizePool: normalizePrizePool(form.prizePool),
        mode: modesDisabled ? null : cleanedModes[0] || form.mode || null,
        modes: cleanedModes,
        startDate: Timestamp.fromDate(new Date(form.startDate)),
        endDate: Timestamp.fromDate(new Date(form.endDate)),
      };
      if (editing) {
        // The visibility bar and the voting control own these once created.
        delete payload.visibility;
        delete payload.votingOpen;
        delete payload.communityAverageVisible;
      }

      let savedId = existing?.id;
      if (editing) {
        await updateTournament(existing.id, payload);
        toast.success('Tournament updated.');
      } else {
        savedId = await createTournament(payload);
        await createStages(
          savedId,
          (STAGE_PRESETS.find((p) => p.id === presetId) || STAGE_PRESETS[0]).build()
        );
        toast.success('Tournament created.');
      }
      onSaved?.({ id: savedId, ...payload });
    } catch (error) {
      toast.error('Save failed.', { description: error.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="grid gap-6">
      <Field label="Cover image">
        <input
          type="file"
          accept="image/*"
          onChange={(e) => setImageUpload(e.target.files?.[0] || null)}
          className="block w-full text-sm file:mr-4 file:rounded-md file:border-0 file:bg-secondary file:px-3 file:py-2 file:text-xs file:font-medium file:uppercase file:tracking-[0.15em] file:text-secondary-foreground hover:file:bg-secondary/80"
        />
        {(imageUpload || form.imageUrl) && (
          <img
            src={imageUpload ? URL.createObjectURL(imageUpload) : form.imageUrl}
            alt="Preview"
            className="mt-3 h-40 w-full rounded-md border border-border/60 object-cover"
          />
        )}
      </Field>

      <Field label="Name" required>
        <Input value={form.name} onChange={handleNameChange} required />
      </Field>

      <Field
        label="URL slug"
        hint={`Shown in the address bar: /tournaments/${form.slug || 'your-slug'} — auto-filled from the name, editable.`}
      >
        <Input
          value={form.slug}
          onChange={(e) => patch('slug')(softSlug(e.target.value))}
          onBlur={(e) => patch('slug')(slugify(e.target.value))}
          placeholder="summer-cup-2025"
        />
      </Field>

      <Field label="Description">
        <Textarea rows={3} value={form.description} onChange={patchValue('description')} />
      </Field>

      {!editing && (
        <Field
          label="Format"
          hint="A starting point. Add, remove and change stages on the tournament page."
        >
          <div role="radiogroup" aria-label="Format" className="grid gap-3 sm:grid-cols-2">
            {STAGE_PRESETS.map((preset) => {
              const selected = presetId === preset.id;
              return (
                <button
                  key={preset.id}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => setPresetId(preset.id)}
                  className={cn(
                    'rounded-md border p-3 text-left transition-colors',
                    selected ? 'border-heat bg-heat/10' : 'border-border/60 hover:border-border'
                  )}
                >
                  <div className="text-sm font-medium">{preset.label}</div>
                  <div className="text-xs text-muted-foreground">{preset.description}</div>
                  <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
                    {preset.build().map(stageTypeLabel).join(' → ')}
                  </div>
                </button>
              );
            })}
          </div>
        </Field>
      )}

      <Field label="Participation link">
        <Input
          value={form.participation}
          onChange={patchValue('participation')}
          placeholder="e.g. Discord signup URL"
        />
      </Field>

      <Field
        label="Stream link"
        hint="Optional. Shown as a Watch stream button on this tournament's page."
      >
        <Input
          type="url"
          value={form.streamUrl || ''}
          onChange={patchValue('streamUrl')}
          placeholder="https://www.twitch.tv/channel"
        />
      </Field>

      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Game">
          <NativeSelect value={form.game} onChange={patchValue('game')}>
            <option value="">No game (multi-game contest)</option>
            {GAMES.map((g) => (
              <option key={g} value={g}>
                {GAME_LABELS[g]}
              </option>
            ))}
          </NativeSelect>
        </Field>
        <Field label="Modes" hint={modesHint}>
          <div
            className={cn(
              'flex flex-wrap gap-2',
              modesDisabled && 'pointer-events-none opacity-50'
            )}
          >
            {MODES.map((m) => {
              const selected = (form.modes || []).includes(m);
              return (
                <button
                  key={m}
                  type="button"
                  onClick={() => {
                    const current = form.modes || [];
                    const next = selected ? current.filter((x) => x !== m) : [...current, m];
                    patch('modes')(next);
                    if (!selected) patch('mode')(m);
                  }}
                  className={cn(
                    'border px-3 py-1 font-mono text-xs uppercase tracking-[0.2em] transition-colors',
                    selected
                      ? 'border-heat bg-heat/10 text-heat'
                      : 'border-border/60 text-muted-foreground hover:border-border hover:text-foreground'
                  )}
                >
                  {modeLabel(m)}
                </button>
              );
            })}
          </div>
        </Field>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Start">
          <Input
            type="datetime-local"
            value={toDateTimeLocal(toDate(form.startDate))}
            onChange={(e) => patch('startDate')(new Date(e.target.value))}
          />
        </Field>
        <Field label="End">
          <Input
            type="datetime-local"
            value={toDateTimeLocal(toDate(form.endDate))}
            onChange={(e) => patch('endDate')(new Date(e.target.value))}
          />
        </Field>
      </div>

      <Field label="Tournament logo">
        <input
          type="file"
          accept="image/*"
          onChange={(e) => setLogoUpload(e.target.files?.[0] || null)}
          className="block w-full text-sm file:mr-4 file:rounded-md file:border-0 file:bg-secondary file:px-3 file:py-2 file:text-xs file:font-medium file:uppercase file:tracking-[0.15em] file:text-secondary-foreground hover:file:bg-secondary/80"
        />
        {(logoUpload || form.tournamentLogo) && (
          <img
            src={logoUpload ? URL.createObjectURL(logoUpload) : form.tournamentLogo}
            alt="Logo preview"
            className="mt-3 h-32 w-32 rounded-md border border-border/60 object-contain bg-muted/20"
          />
        )}
      </Field>

      <Field
        label="Sponsors"
        hint="Names, optional links, and logos shown in the public prize/sponsor section."
      >
        <div className="grid gap-3">
          {(form.sponsors || []).map((sponsor, index) => (
            <div
              key={`sponsor-${index}`}
              className="grid gap-3 rounded-md border border-border/60 p-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]"
            >
              <label className="flex flex-col gap-1.5">
                <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                  Name
                </span>
                <Input
                  value={sponsor.name || ''}
                  onChange={(event) => updateSponsor(index, 'name', event.target.value)}
                  placeholder="Sponsor name"
                  aria-label={`Sponsor ${index + 1} name`}
                />
              </label>
              <label className="flex flex-col gap-1.5">
                <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                  Link (optional)
                </span>
                <Input
                  type="url"
                  value={sponsor.link || ''}
                  onChange={(event) => updateSponsor(index, 'link', event.target.value)}
                  placeholder="https://sponsor.example"
                  aria-label={`Sponsor ${index + 1} link`}
                />
              </label>
              <div className="flex flex-wrap items-center gap-3 sm:col-span-2">
                {sponsor.logoUrl && (
                  <img
                    src={sponsor.logoUrl}
                    alt={sponsor.name || `Sponsor ${index + 1}`}
                    className="h-16 w-28 rounded-md border border-border/60 bg-muted/20 object-contain p-2"
                  />
                )}
                <label className="flex min-w-0 flex-1 flex-col gap-1.5">
                  <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                    Logo upload
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(event) => updateSponsorLogo(index, event.target.files?.[0] || null)}
                    className="block w-full text-sm file:mr-4 file:rounded-md file:border-0 file:bg-secondary file:px-3 file:py-2 file:text-xs file:font-medium file:uppercase file:tracking-[0.15em] file:text-secondary-foreground hover:file:bg-secondary/80"
                    aria-label={`Sponsor ${index + 1} logo upload`}
                  />
                  {sponsor.logoFile && (
                    <span className="text-xs text-muted-foreground">{sponsor.logoFile.name}</span>
                  )}
                </label>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeSponsor(index)}
                  aria-label={`Remove sponsor ${index + 1}`}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
          <Button type="button" variant="outline" className="w-fit" onClick={addSponsor}>
            <Plus className="h-4 w-4" />
            Add sponsor
          </Button>
        </div>
      </Field>

      <Field
        label="Prize pool"
        hint="Choose whether the prize pool is absent, undisclosed, or published with a contributor breakdown."
      >
        <PrizePoolEditor value={form.prizePool} onChange={patch('prizePool')} />
      </Field>

      <Field
        label="Rules"
        hint="Rich text shown on the tournament detail page. Use the toolbar's image button to upload and embed images."
      >
        <TournamentRulesEditor value={form.rules} onChange={patch('rules')} />
      </Field>

      <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={saving}>
          {saving ? 'Saving…' : editing ? 'Save changes' : 'Create tournament'}
        </Button>
      </div>
    </form>
  );
};

export default TournamentSettingsForm;
